// Release history for the "What's New" page, newest month first. Each month is
// grouped into themed sections so admins/site managers can scan what shipped
// without reading raw commit messages. Every item is tagged New or Fixed;
// untagged items are minor polish/copy changes that don't warrant either label.
//
// Adding an entry: drop a `{ type, text }` item into the right month's group
// (or add a new group). `type` is 'new', 'fix', or null. Text is customer-facing
// prose — CMOS style, closed-up em dashes, "TALON" uppercase. Keep it to one
// sentence that describes the change itself. Don't recount what the old
// behavior was: it's rarely what the reader needs and it makes the page harder
// to scan. Rationale, formulas, and field-by-field behavior belong in
// KPI_FORMULAS.md, not here — this page is for scanning. A change that genuinely
// needs more than one sentence is usually two entries. Counts on the page
// derive automatically. Newest first, everywhere: the newest month stays at the
// top of this array (it renders as "Latest"), and within a month the newest group
// (and newest items within a group) go at the top — so anyone opening What's New
// sees the latest work first.
export const months = [
  {
    id: '2026-10',
    label: 'October 2026',
    groups: [
      {
        title: 'Service',
        items: [
          { type: 'fix', text: 'Parts to Labor Ratio now divides parts sold on repair orders by the labor hours on those orders. It no longer factors in the posted labor rate.' }
        ]
      }
    ]
  },
  {
    id: '2026-08',
    label: 'August 2026',
    groups: [
      {
        title: 'Service',
        items: [
          { type: 'new', text: 'Performance Metrics now reports service transactions—average hours booked per repair order and the value of those hours at your labor rate.' },
          { type: 'new', text: 'Service Input now includes parts sold on repair orders. The figure drives insight into the parts to labor ratio.' },
          { type: 'fix', text: 'Fixed bug where service gross profit displayed estimated percent values.' },
          { type: 'fix', text: 'Fixed bug where select views displayed 100 percent of service revenue as gross profit, overstating the total.' }
        ]
      },
      {
        title: 'Sales & F&I',
        items: [
          { type: 'new', text: 'Penetration and average-income benchmarks for each F&I product can now be set per dealership under Targets, and the F&I Scoreboard measures against them.' },
          { type: 'new', text: 'F&I managers and sales managers can now be named under Dropdown Manager and picked on F&I Input, so deals can be credited to staff who don’t hold a Masters Path login.' },
          { type: 'new', text: 'A new F&I Scoreboard ranks F&I managers, tracks penetration and average income for ESP, PPM, Tire, GAP, Theft, and Appearance against monthly targets, and shows dealership F&I both against the month’s plan and against the units actually delivered.' },
          { type: 'new', text: 'Trade-ins are now entered as a dollar amount rather than a yes/no, and the difference against actual cash value—the over/under—is charged against that bike’s gross. Average over/under per bike appears on the Sales Performance dashboard, the sales scoreboards, and Profit Summary.' },
          { type: 'new', text: 'Dealerships that charge a doc fee can now set it once under Targets—separately for new and used, so you can charge it on one, both, or neither, at different amounts—and it counts toward back-end (F&I) gross profit on every bike sold, flowing through the dashboards, the sales scoreboards, Profit Summary, and Overview. F&I Input shows the amount read-only on each deal.' }
        ]
      },
      {
        title: 'Overview & Scoreboards',
        items: [
          { type: 'fix', text: 'Fixed Operations Performance: Service RO now counts repair orders rather than daily entries.' },
          { type: 'fix', text: 'Repair Orders on the Fixed Operations Scoreboard now counts repair orders.' },
          { type: 'fix', text: 'Month-to-date on the Fixed Operations Scoreboard now measures through the date you’re viewing.' },
          { type: 'fix', text: 'The Fixed Operations Scoreboard now shows month-to-date only.' },
          { type: 'fix', text: 'Overview, the scoreboards, and the Fixed Ops dashboard now agree with Profit Summary on which month an entry belongs to. Depending on your time zone, an entry dated the first of the month could be counted in the month before it, so the same figure read differently from one page to the next.' },
          { type: 'fix', text: 'The main Sales Performance and Fixed Operations gauges now color themselves against the month you’re viewing—a completed month was previously measured against days elapsed in the current one, so it read green no matter how it actually closed.' }
        ]
      },
      {
        title: 'Profit Summary',
        items: [
          { type: 'fix', text: 'Parts to Labor Ratio corrected to measure parts sold on closed repair orders against the related booked hours, rather than the open-RO figures entered on Inventory Input.' },
          { type: 'fix', text: 'Effective Labor Rate now uses the technician count from Targets unless Daily Technicians’ Performance is logged on at least half the working days in the period.' },
          { type: 'new', text: 'Transaction benchmarks are now set per dealership under Targets—average line items and dollars per transaction for parts and apparel, plus the parts to labor ratio—and Profit Summary colors each value against them. Every dealership starts at the standard benchmarks, so nothing changes until you set your own.' },
          { type: 'new', text: 'Hovering a category heading on Profit Summary—New Units, Used Units, Front End Gross Profit, or F&I Gross Profit—now clarifies what that section counts.' },
          { type: 'new', text: 'Profit Summary now reports front-end gross: total dollars for new, used, and combined, plus the average per unit for each. Front end is unit gross only—back-end F&I stays in its own section, where new and used are always combined.' },
          { type: 'new', text: 'Front-end gross and F&I now show year-over-year alongside their forecast and variance, matching the year-over-year already reported for Service, P&A, and Apparel.' },
          { type: 'fix', text: 'Year-over-year now compares month-to-date against the same days last year rather than against the full prior-year month. Every year-over-year figure read low until the month closed.' },
          { type: 'fix', text: 'The F&I forecast now counts both back-end target fields—reserve and products—so the forecast and variance match the Sales Performance dashboard. Profit Summary previously counted only products, understating the forecast.' },
          { type: 'new', text: 'Effective Labor Rate now measures service revenue against staffed technician capacity—working days times eight hours times the technician count—rather than against hours logged on repair orders, and Profit Summary and the Fixed Ops Performance dashboard report the same rate.' },
          { type: 'new', text: 'Effective Labor Rate, Technician Proficiency, and the Service, Parts, and Apparel gross margins are now color-coded against their targets—green at 91 percent and above, yellow from 80 to 90, red below—and hovering any of them shows the figures behind the number.' },
          { type: 'fix', text: 'Effective Labor Rate reads “Not Available” instead of $0 when a dealership has neither daily technician entries nor a technician count in Targets, so an unconfigured field no longer looks like a real result.' }
        ]
      }
    ]
  },
  {
    id: '2026-07',
    label: 'July 2026',
    groups: [
      {
        title: 'Inventory Input',
        items: [
          { type: 'fix', text: 'Staff with inventory access can now save the Service Open Repair Orders section—previously that one grouping was limited to owners and managers, so other users hit a permissions error when submitting it.' }
        ]
      },
      {
        title: 'F&I Input',
        items: [
          { type: 'new', text: 'F&I pricing fields now accept cents, so amounts like $35,317.40 can be entered exactly.' },
          { type: 'new', text: 'Added safeguards to F&I pricing: an out-of-range cost or price is blocked, an unusually large gross loss is flagged as you enter it, and closing such a deal asks you to confirm—so one mistyped number can’t skew the dashboards.' }
        ]
      },
      {
        title: 'Sales Performance & Dashboards',
        items: [
          { type: 'new', text: 'Every scoreboard and dashboard now respects the month you’re looking at—pick a past month and targets, forecasts, and leaderboards recalculate for that month instead of always showing the current one.' },
          { type: 'new', text: 'Added Sales forecast rows to Performance Metrics.' },
          { type: 'fix', text: 'The Sales leaderboard no longer goes blank on the 1st of a new month—it follows the selected date instead of a live “current month” that has no data yet.' },
          { type: 'fix', text: 'The leaderboard keeps its card and shows an inline “no data” message instead of blanking the whole page when a period has no sales.' },
          { type: 'fix', text: 'The Fixed Ops Scoreboard scrolls instead of collapsing when it’s embedded in another view.' }
        ]
      },
      {
        title: 'Motorcycle & Dropdown Management',
        items: [
          { type: 'new', text: 'Motorcycle dropdown categories can now be added, renamed, and deleted, with drag-and-drop to move models between categories.' },
          { type: 'new', text: 'Added a Delete action to F&I entries, with a confirmation prompt before anything is removed.' },
          { type: 'fix', text: 'Archived makes and models can be reactivated, and a model being dragged stays visible as it moves between categories.' },
          { type: 'fix', text: 'Makes now show up before any model has been added under them, and adding a new category auto-creates its model list.' }
        ]
      }
    ]
  },
  {
    id: '2026-06',
    label: 'June 2026',
    groups: [
      {
        title: 'Platform Roles & Access',
        items: [
          { type: 'new', text: 'Added the Site Manager role, for platform staff who create and manage owners and dealerships without full admin access.' },
          { type: 'new', text: 'Added admin impersonation (“View as”)—support staff can temporarily view the platform as a specific user, with a banner and one click back to their own session.' },
          { type: 'new', text: 'General Managers can now be granted dealership-scoped Manage Users and Edit Dropdowns permissions, instead of needing full owner access.' },
          { type: 'new', text: 'GMs with a management permission now see the activity alert bell that was previously owner-only.' },
          { type: 'new', text: 'New dealership users can be activated immediately with a temporary password, skipping the email invite step (admins and site managers only).' },
          { type: 'fix', text: 'Platform staff can edit a user’s role without needing to own that user’s dealership.' },
          { type: 'fix', text: 'Switching identity via impersonation no longer leaves stale dashboard state behind.' }
        ]
      },
      {
        title: 'Accounts & Invitations',
        items: [
          { type: 'new', text: 'Owners can now be invited with an optional password set at creation, and invites can be resent.' },
          { type: 'new', text: 'Added a self-service password reset page.' },
          { type: 'new', text: 'Replaced browser pop-up confirmations with the platform’s branded dialogs throughout account management.' },
          { type: 'fix', text: 'Email lookups are now case-insensitive, so accounts no longer fail to match on capitalization.' }
        ]
      },
      {
        title: 'Dropdown & Template Management',
        items: [
          { type: 'new', text: 'Added an in-app dropdown template editor, styled to match the rest of the platform, so site managers can manage dealership dropdown templates directly.' },
          { type: 'new', text: 'New dealerships now have the default dropdown template applied automatically on creation.' }
        ]
      },
      {
        title: 'Daily Log',
        items: [
          { type: 'new', text: 'GMs, F&I Managers, and other non-Fit-Specialist roles can now edit or delete any entry; Fit Specialists remain limited to their own entries.' },
          { type: 'new', text: 'Anyone with form access—not just Fit Specialists—can log or select entries for a named specialist, and non-specialist roles now see all entries, including named-specialist rows.' },
          { type: 'new', text: 'Adopted the platform’s branded date picker on entry forms.' },
          { type: 'fix', text: 'The dealership picker on Daily Log works correctly in managed mode.' },
          { type: 'fix', text: 'Logger action buttons no longer stack vertically, and the Edit button is more visible.' }
        ]
      },
      {
        title: 'F&I Reporting',
        items: [
          { type: 'new', text: 'Profit summary now includes F&I Gross Profit (Reserve, Product, Total, and Per Unit) and Average Left on Table per Bike.' },
          { type: 'new', text: 'Added a % Certified metric (used bikes only); new bikes can no longer be marked certified by mistake.' },
          { type: 'new', text: 'Added a per-dealership toggle to always include unclosed F&I deals in reporting; when it’s off, unclosed deals are excluded consistently across every dashboard.' },
          { type: 'new', text: 'Sales Performance now includes F&I Reserve/Product targets and per-sale funnel KPIs.' },
          { type: 'fix', text: 'F&I saves that were silently failing now go through, and an entry that fails to close stays saved as open instead of losing the edit.' },
          { type: 'fix', text: 'Closing an F&I deal now requires explicit zeros where a value is genuinely zero, so an unentered field can’t be mistaken for an entered $0.' }
        ]
      },
      {
        title: 'Data Reliability',
        items: [
          { type: 'fix', text: 'Consolidated demo/sample data generation into a single system, so every dashboard shows coherent, realistic figures instead of occasionally mismatched or zeroed-out values.' },
          { type: 'fix', text: 'Fixed a crash on the F&I form when dropdown data was temporarily unavailable, and added platform-wide error handling so a single broken screen no longer takes down the app.' },
          { type: 'fix', text: 'Forecast-to-date targets now prorate correctly for every dealership, not just the demo account.' }
        ]
      },
      {
        title: 'Overview & Scoreboards',
        items: [
          { type: 'new', text: 'General Managers now see the same aggregate overview dashboard owners do, scoped to their own dealership, with no empty “Recent Activity” widget.' },
          { type: 'new', text: 'Overview gained month look-back, so past months can be reviewed the same way Performance Metrics already allowed.' },
          { type: 'new', text: 'Sales and Fixed Ops dashboards got per-period status flags, 4-period support for Fixed Ops, and a more compact layout.' },
          { type: 'fix', text: 'Sales and Fixed Ops scoreboards are now correctly scoped to the dealerships of the owner being viewed.' },
          { type: 'fix', text: 'Added the Transactions section to the single-dealer Sales view, which was missing it.' }
        ]
      },
      {
        title: 'Public Pages & Login',
        items: [
          { type: 'new', text: 'Added a public Support page with a contact form, routed to a configurable recipient from Admin → Support.' },
          { type: 'new', text: 'Added a “Request an Account” page with a validated contact form, reachable from the login screen.' },
          { type: 'new', text: 'Both public forms are protected against spam with a captcha and rate limiting.' },
          { type: 'new', text: 'Added a Support link to the logged-in user menu.' },
          { type: 'new', text: 'Reworked the login screen’s marketing panel with pop-in screens, a dedicated mobile layout, and looping autoplay.' }
        ]
      }
    ]
  },
  {
    id: '2026-05',
    label: 'May 2026',
    groups: [
      {
        title: 'Account & Security',
        items: [
          { type: 'new', text: 'Added an Account Settings page where users can update their password; new passwords must meet strength requirements.' },
          { type: 'fix', text: 'Sessions now redirect cleanly to login on timeout instead of leaving a broken screen.' }
        ]
      },
      {
        title: 'Help & Support',
        items: [
          { type: 'new', text: 'Added a Help & Resources section with guides for getting around the platform.' }
        ]
      },
      {
        title: 'Fit Specialist Tracking',
        items: [
          { type: 'new', text: 'Named Fit Specialists are now included in leaderboards and scoreboards, alongside a new dropdown control to manage the Fit Specialist list per dealership.' }
        ]
      },
      {
        title: 'Admin Tools',
        items: [
          { type: 'new', text: 'Admins can now move or copy a dealership between owners and change an owner’s email address.' },
          { type: 'new', text: 'Added beta feature controls so specific features can be turned on for individual roles ahead of a full release.' },
          { type: 'new', text: 'Moved the demo dealership control into the Admin Panel.' }
        ]
      },
      {
        title: 'Polish',
        items: [
          { type: null, text: 'Moved Overview alerts into the notifications bell icon.' },
          { type: null, text: 'Condensed the Overview page heading and refreshed the platform logo.' }
        ]
      }
    ]
  },
  {
    id: '2026-03',
    label: 'March 2026',
    groups: [
      {
        title: 'Compliance',
        items: [
          { type: 'new', text: 'Added Terms of Service and Privacy Policy pages.' }
        ]
      },
      {
        title: 'Performance Metrics',
        items: [
          { type: null, text: 'Renamed Profit Summary to Performance Metrics.' }
        ]
      },
      {
        title: 'Accounts',
        items: [
          { type: 'new', text: 'Added email verification for newly created users.' },
          { type: 'fix', text: 'Fixed invite email branding and a case where changing a user’s email or deleting a user didn’t update correctly.' }
        ]
      }
    ]
  },
  {
    id: '2026-02',
    label: 'February 2026',
    groups: [
      {
        title: 'Dropdown & Motorcycle Management',
        items: [
          { type: 'new', text: 'Admins can manage dropdown field templates and motorcycle model families platform-wide.' },
          { type: 'new', text: 'Added the ability to reorder motorcycle makes and models, and to edit dropdown entries directly from the dealership management screen.' },
          { type: 'new', text: 'Added searchable dropdown fields to the F&I form.' }
        ]
      },
      {
        title: 'Performance Metrics',
        items: [
          { type: 'fix', text: 'Connected Open ROs, Labor Sales, and Parts-to-Labor Ratio to live data instead of placeholders.' },
          { type: 'new', text: 'Added color coding (red/yellow/green) to the Effective Labor Rate metric based on performance.' }
        ]
      },
      {
        title: 'Fixed Operations',
        items: [
          { type: 'new', text: 'Added Available Hours to labor input, and new labor analysis cards for lost time and lost opportunity.' }
        ]
      },
      {
        title: 'Polish',
        items: [
          { type: null, text: 'Style updates to KPI cards across dashboards for readability.' }
        ]
      }
    ]
  },
  {
    id: '2026-01',
    label: 'January 2026',
    groups: [
      {
        title: 'Performance Metrics (formerly Profit Summary)',
        items: [
          { type: 'new', text: 'Launched the Performance Metrics page, with sales data and red/yellow/green performance styling.' },
          { type: 'fix', text: 'Corrected revenue forecast values on the Performance Metrics page.' }
        ]
      },
      {
        title: 'Sales Leader Scoreboard',
        items: [
          { type: 'new', text: 'Added the Sales Leader Scoreboard, built for dealership display, with new/used bike KPIs and a leaderboard ranking.' },
          { type: 'new', text: 'Added test rides and deal add-on KPI cards to the sales performance dashboard.' },
          { type: 'fix', text: 'Fixed duplicate same-day entries and leaderboard ranking accuracy.' }
        ]
      },
      {
        title: 'Fixed Operations',
        items: [
          { type: 'new', text: 'Added Net Sales, Inventory Turns, and Effective Labor Rate cards to the Fixed Operations dashboard.' },
          { type: 'new', text: 'Moved Fixed Ops units sold with bikes onto the Fixed Ops dashboard.' },
          { type: 'new', text: 'Added service technician tracking to targets.' }
        ]
      },
      {
        title: 'F&I Tracking',
        items: [
          { type: 'new', text: 'Added F&I Reserve and F&I Product Gross Profit to the sales dashboard, with supporting backend calculations.' }
        ]
      },
      {
        title: 'Data Entry',
        items: [
          { type: 'new', text: 'Expanded weekly inventory input, added NPS input, and added labor data with a tabbed table view for service input.' },
          { type: 'fix', text: 'Added validation to prevent submitting blank fields on partial entries, and protection against duplicate entries for the same dealership and day.' }
        ]
      },
      {
        title: 'Reliability',
        items: [
          { type: 'fix', text: 'Fixed several dashboard date-range bugs, missing targets, and a role-permission issue that hid menu items for some users.' }
        ]
      }
    ]
  }
];
